/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import {
  PropsWithChildren,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useMemo,
  forwardRef,
} from 'react';
import { getHighlightRGBA } from '../utils/utils';
import styles from '../styles/Highlight.module.css';
import { Annotation } from '../../Annotation/types';

interface MarkElementProps {
  annotation: Annotation;
  onClick: (event: ReactMouseEvent) => void;
  onKeyDown: (event: ReactKeyboardEvent) => void;
}

const INTERACTIVE_ELEMENTS = ['a', 'button', 'input', 'select', 'textarea'];

const MarkElement = forwardRef<
  HTMLElement,
  PropsWithChildren<MarkElementProps>
>(({ annotation, children, onClick, onKeyDown, ...props }, ref) => {
  const noteContent = annotation.note;
  const hasNote = typeof noteContent === 'string' && noteContent.length > 0;

  const handleClick = (event: ReactMouseEvent) => {
    // Don't interfere with nested interactive elements
    if (
      (event.target as HTMLElement).closest(INTERACTIVE_ELEMENTS.join(', '))
    ) {
      return;
    }
    onClick(event);
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    // Don't interfere with nested interactive elements
    if (
      (event.target as HTMLElement).closest(INTERACTIVE_ELEMENTS.join(', '))
    ) {
      return;
    }
    onKeyDown(event);
  };

  // Build comprehensive aria-label with newlines for better separation
  const fullAriaLabel = useMemo(() => {
    let label = 'Highlighted text';
    if (hasNote && noteContent) {
      label += ` with note: ${noteContent}\n\n`;
    } else {
      label += '. ';
    }
    label += 'Press Enter or Space to edit or remove highlight.';
    return label;
  }, [hasNote, noteContent]);

  return (
    <mark
      {...props}
      ref={ref}
      role='button'
      className={styles.interactiveHighlight}
      tabIndex={0}
      aria-label={fullAriaLabel}
      aria-haspopup='menu'
      aria-roledescription='highlight annotation'
      data-highlight-element='true'
      style={
        {
          '--highlight-bg': getHighlightRGBA(annotation.display.color),
          '--highlight-hover-bg': getHighlightRGBA(
            annotation.display.color,
            'focus',
          ),
        } as React.CSSProperties
      }
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </mark>
  );
});

MarkElement.displayName = 'MarkElement';

export default MarkElement;
